// Context menu entries of the compta screen. Document rows/tiles carry `data-ctx="doc" data-ctx-id="…"` and a
// `data-ctx-scope` (added by PATCHES in scripts/compile-dc.mjs); the entries call what the screen already offers
// (row click = its own handler, the pay button's handler) and the existing endpoints: file URLs, status changes
// (POST :id/status, per-type transitions from data/status.ts), taking a payment back (DELETE compta/payments/:id) and
// the edit and delete dialogs (components/edit). Writes respect the user's EDIT permission of the document type / p_mark.
import { DOCUMENT_PERMISSION_KEYS } from '@rimon/contracts';
import { tr2 } from '../../i18n/lang';
import { level, session } from '../../api/auth';
import { errorText } from '../../api/client';
import { live, reloadDocs, type ApiDocument, type ApiQueueItem } from '../../data/compta';
import { isPayable, nextStatuses, statusText, type DocStatus, type DocType } from '../../data/status';
import { openDocEdit } from '../edit/DocEditDialog';
import { openDocDelete } from '../edit/DocDeleteDialog';
import type { DocDetail } from '../edit/docEdit';
import * as A from '../../data/actions';
import { downloadAnnotated, downloadFile, fileList, openFile, printAnnotated, printFile } from '../docFiles';
import { shortcut, type CtxEntry } from '@rimocore/shared/ctx/menu';
import type { CtxEvent, CtxProvider } from '@rimocore/shared/ctx/registry';
import { copyAndTell } from '@rimocore/shared/ctx/text';
import { toast } from './toast';
import { openBinder, type BinderGroup } from '../print/BinderDialog';
import { openPrintOverview } from '../print/PrintOverview';
import { openRecurring } from '../recurring/RecurringDialog';
import { openOrders } from '../orders/OrderDialog';

/** The compta screen's logic instance (DCHost.logic), loosely typed: logic.ts is untyped (@ts-nocheck). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ComptaLogic = any;

type Doc = ApiDocument | ApiQueueItem;
/** GET compta/documents/:id results (payments, reconciliation), fetched when a menu opens; short-lived. */
const fetched = new Map<string, { d: ApiDocument & Partial<DocDetail>; at: number }>();

/** Document by id from what the screen already loaded (no request). */
export function findDoc(id: string): Doc | undefined {
  const lists: Doc[][] = [live.queue, live.workflow, live.creditNotes, live.search?.docs ?? [],
    ...Object.values(live.rows).flatMap((m) => Object.values(m ?? {}))];
  for (const l of lists) { const d = l.find((x) => x && x.id === id); if (d) return d; }
  return fetched.get(id)?.d;
}
/** Stores a loaded document detail (also used by tests). */
export const rememberDetail = (d: ApiDocument & Partial<DocDetail>) => { fetched.set(d.id, { d, at: Date.now() }); };
const detailOf = (id: string) => { const f = fetched.get(id); return f && Date.now() - f.at < 15_000 ? f.d : undefined; };
/** Loads a document the store does not have (Réception list, paid today); the menu rebuilds when it arrives. */
export function fetchDoc(id: string): Promise<ApiDocument | null> {
  return A.getDocument(id).then((d) => { fetched.set(id, { d, at: Date.now() }); if (fetched.size > 50) fetched.delete(fetched.keys().next().value!); return d; }, () => null);
}

const titleOf = (d: Doc | undefined, no?: string) => [d?.documentNumber || no, d?.issuerName].filter(Boolean).join(' · ') || tr2('Dokument', 'Document');
const fileName = (d: Doc | undefined, no?: string) => [d?.issuerName, d?.documentNumber || no].filter(Boolean).join('_').replace(/\s+/g, '_') || 'Dokument';
export { openFile as openBig, downloadFile as download, printFile as print } from '../docFiles';

/** What a click on the row does in its list (scope from the template patch). */
function rowClickLabel(scope: string, logic: ComptaLogic, id: string): string | null {
  switch (scope) {
    case 'browser': case 'pay': return tr2('Auswählen', 'Sélectionner');
    case 'search': return tr2('Im Monat zeigen', 'Afficher dans le mois');
    case 'prep': return tr2('Rechts in der Vorschau zeigen', 'Afficher dans l’aperçu');
    case 'recu': return logic?.state?.picked?.[id] ? tr2('Aus der Auswahl nehmen', 'Retirer de la sélection') : tr2('Für die Vorbereitung auswählen', 'Sélectionner pour la préparation');
    default: return null;
  }
}
const selectedId = (scope: string, logic: ComptaLogic): string | undefined =>
  scope === 'pay' ? logic?._paySelRow?.id : scope === 'browser' ? logic?._browserSel?.id : scope === 'prep' ? logic?._prepPrev?.id : undefined;

const canEdit = (type: DocType) => level(session.user, DOCUMENT_PERMISSION_KEYS[type]) === 'EDIT';
const done = (text: string) => { toast(text); reloadDocs().catch(() => {}); };
const failed = (e: unknown) => toast(errorText(e), 'warning');

/** Status changes that fit the type (data/status.ts); paid / not paid of payable types goes through payments. */
export function statusEntries(d: { id: string; version: number; type: DocType; status: DocStatus }, reconciled: boolean): CtxEntry[] {
  if (!canEdit(d.type) || reconciled) return [];
  return nextStatuses(d.type, d.status)
    .filter((s) => s.key !== 'ARCHIVED' && s.key !== 'RECONCILED' && !(isPayable(d.type) && (s.key === 'PAID' || d.status === 'PAID')))
    .map((s) => {
      const label = tr2(s.de, s.fr);
      return { label, icon: s.key === 'PROBLEM' ? 'close' : 'check', danger: s.key === 'PROBLEM',
        run: () => A.transition({ id: d.id, version: d.version }, s.key as DocStatus).then(() => done(tr2(`Status: ${label}`, `Statut : ${label}`)), failed) } as CtxEntry;
    });
}

/** Entries for a document row/tile. `rebuild` re-opens the menu once the document's details have been loaded. */
export function docEntries(el: HTMLElement, logic: ComptaLogic, rebuild: () => void): CtxEntry[] {
  const id = el.dataset.ctxId, scope = el.dataset.ctxScope || '', no = el.dataset.ctxNo;
  if (!id) return [];
  const det = detailOf(id), d = det ?? findDoc(id);
  if (!det) void fetchDoc(id).then((x) => x && rebuild());
  const title = titleOf(d, no), name = fileName(d, no);
  const can = (k: string) => !logic || typeof logic.can !== 'function' || logic.can(k);
  const sel = selectedId(scope, logic) === id;
  const click = rowClickLabel(scope, logic, id);
  const q = d && 'suggestedAmount' in d ? d : null;
  const bgl = scope === 'pay' && logic?.state?.payAcct === 'BGL';
  const amt = amountText(scope === 'pay' && q ? (q.plannedAmount ?? q.suggestedAmount) : d ? (d.effectiveAmount ?? d.totalAmount) : null, bgl ? '.' : ',');
  const comm = q ? (q.plannedCommunication || q.communication) : d?.communication;
  const number = d?.documentNumber || (no && no !== '—' ? no : null);
  const type = d?.type as DocType | undefined, editable = !!type && type !== 'BANK_STATEMENT' && canEdit(type);
  const reconciled = !!det?.reconciledWith?.length;
  const payment = det?.payments?.length ? [...det.payments].sort((a, b) => (a.paidAt < b.paidAt ? 1 : -1))[0] : undefined;
  const out: Array<CtxEntry | null | false | undefined | ''> = [
    { header: title },
    { label: tr2('Öffnen · große Ansicht', 'Ouvrir · grand aperçu'), icon: 'expand', run: () => openFile(id, title) },
    click && !(sel && (scope === 'browser' || scope === 'pay' || scope === 'prep')) && { label: click, icon: scope === 'recu' ? 'check' : 'open', run: () => el.click() },
    editable && { label: tr2('Bearbeiten …', 'Modifier …'), icon: 'text', run: () => openDocEdit(id) },
    'sep',
  ];
  // Bezahlung: the selected row can be booked with the page's own button handler (same checks, same undo toast).
  if (scope === 'pay' && sel && can('p_mark') && typeof logic?.pay === 'function') {
    const acct = logic.state?.payAcct || 'BIL';
    out.push({ label: tr2(`Als bezahlt markieren · ${acct}`, `Marquer comme payé · ${acct}`), icon: 'pay', run: () => { const p = logic.pay(); if (p && typeof p.pay === 'function') p.pay(); } });
  }
  // „Status ▸“: the current status as hint, the possible next ones in the submenu (like macOS / Windows menus).
  if (d && type && det) {
    const next = statusEntries({ id, version: det.version, type, status: det.status as DocStatus }, reconciled);
    if (next.length) out.push({ label: 'Status', icon: 'check', hint: statusText(type, det.status as DocStatus, reconciled), sub: next });
  }
  if (payment && det && level(session.user, 'p_mark') === 'EDIT' && !reconciled)
    out.push({ label: tr2('Zahlung zurücknehmen', 'Annuler le paiement'), icon: 'back', danger: true,
      run: () => A.revertPayment(payment.id, det.version).then(() => done(tr2('Zahlung zurückgenommen.', 'Paiement annulé.')), failed) });
  // LP: „IBAN geändert ▸“ – the IBAN as hint, confirmation after the phone call (right p_mark), written to the journal.
  if (det?.ibanAlert && det.iban && level(session.user, 'p_mark') === 'EDIT') {
    const iban = det.iban.replace(/(.{4})/g, '$1 ').trim();
    out.push({ label: det.ibanAlert === 'new' ? tr2('Neue IBAN', 'Nouvel IBAN') : tr2('IBAN geändert', 'IBAN modifié'), icon: 'phone', hint: iban.slice(0, 14) + '…', sub: [
      { label: iban, icon: 'hash', run: () => copyAndTell(det.iban!, 'IBAN') },
      { label: tr2('IBAN telefonisch bestätigt', 'IBAN confirmé par téléphone'), icon: 'check', run: () => A.confirmIban(id).then(() => done(tr2('IBAN bestätigt · im Protokoll vermerkt.', 'IBAN confirmé · noté au journal.')), failed) },
    ] });
  }
  // BL2/BL3: a supplier invoice and the supplier's orders in RimoChantiers (suggestion, assignment, net comparison).
  if (type === 'INCOMING_INVOICE' && det && level(session.user, 'd_in') !== 'NONE')
    out.push({ label: tr2('Bestellung zuordnen …', 'Rattacher à une commande …'), icon: 'link', run: () => openOrders(id, title) });
  // Files: with attachments a „Dateien ▸“ submenu opens each one; otherwise download/print the PDF directly.
  const files = fileList(det?.files ?? (d as ApiDocument | undefined)?.files);
  out.push('sep');
  if (files.length > 1) {
    out.push({ label: tr2('Dateien', 'Fichiers'), icon: 'docs', hint: String(files.length), sub: files.map((f) => ({ label: f.label, icon: f.primary ? 'docs' : 'open', run: () => openFile(id, f.primary ? title : f.label, f.fileId) } as CtxEntry)) });
  }
  // ES correction (user 02.11.2026): an incoming invoice prints and downloads WITH its notes (escompte box while it has an
  // escompte, „Payée“ stamp once paid) – a new PDF; the unchanged file only as „Original herunterladen“.
  const notes = type === 'INCOMING_INVOICE' && !!det && files.some((f) => f.primary && /pdf/.test(f.file?.mimeType ?? 'application/pdf'));
  out.push(
    { label: tr2('PDF herunterladen', 'Télécharger le PDF'), icon: 'download', run: () => (notes ? downloadAnnotated(id) : downloadFile(id, name)) },
    { label: tr2('Drucken', 'Imprimer'), icon: 'print', hint: shortcut('P'), run: () => (notes ? printAnnotated(id) : printFile(id, name)) });
  if (notes) out.push({ label: tr2('Original herunterladen', 'Télécharger l’original'), icon: 'download', run: () => downloadFile(id, name) });
  // „Kopieren ▸“: number, amount, communication, name.
  const copies = [
    number && { label: tr2('Nummer', 'Numéro'), icon: 'hash', hint: number, run: () => copyAndTell(number, tr2('Nummer', 'Numéro')) },
    amt && { label: tr2('Betrag', 'Montant'), icon: 'euro', hint: amt, run: () => copyAndTell(amt, tr2('Betrag', 'Montant')) },
    comm && { label: 'Communication', icon: 'text', hint: comm.length > 18 ? comm.slice(0, 17) + '…' : comm, run: () => copyAndTell(comm, 'Communication') },
    d?.issuerName && { label: tr2('Name', 'Nom'), icon: 'copy', hint: d.issuerName.length > 18 ? d.issuerName.slice(0, 17) + '…' : d.issuerName, run: () => copyAndTell(d.issuerName!, tr2('Name', 'Nom')) },
  ].filter(Boolean) as CtxEntry[];
  if (copies.length) out.push({ label: tr2('Kopieren', 'Copier'), icon: 'copy', sub: copies });
  // „Löschen …“ last, in red: the dialog (components/edit/DocDeleteDialog) asks first and explains paid / reconciled.
  if (editable) out.push('sep', { label: tr2('Löschen …', 'Supprimer …'), icon: 'trash', danger: true, run: () => openDocDelete(id) });
  return out.filter(Boolean) as CtxEntry[];
}

/** Empty area: only „Neu laden“ (the menu is about the thing under the cursor; no page navigation). */
export function appEntries(): CtxEntry[] {
  return [{ label: tr2('Neu laden', 'Recharger'), icon: 'reload', hint: shortcut('R'), run: () => location.reload() }];
}

/** Selected text outside fields: also search it in the documents. */
export function selectionEntries(ev: CtxEvent, logic: ComptaLogic): CtxEntry[] | null {
  const t = ev.selection.trim();
  if (!t || t.length > 80 || !logic || typeof logic.renderVals !== 'function') return null;
  if (typeof logic.pageVis === 'function' && !logic.pageVis('browser')) return null;
  const shown = t.length > 24 ? t.slice(0, 23) + '…' : t;
  return [{ label: tr2(`In Dokumenten suchen: „${shown}“`, `Rechercher dans les documents : « ${shown} »`), icon: 'search', run: () => logic.renderVals().onGq({ target: { value: t } }) }];
}

/** Global provider for the compta screen; `getLogic` returns the mounted DCHost's logic (or null). */
export function comptaProvider(getLogic: () => ComptaLogic | null, rebuild: () => void): CtxProvider {
  return (ev) => {
    const el = ev.target.closest<HTMLElement>('[data-ctx="doc"]');
    if (!el) return null;
    return docEntries(el, getLogic(), rebuild);
  };
}

/** Amount for the clipboard: decimal string „1805.75“ → „1805,75“ (no thousands separator, pastes into bank forms). */
export function amountText(decimal: string | null | undefined, sep: ',' | '.' = ','): string | null {
  if (decimal == null) return null;
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(String(decimal).trim());
  if (!m) return null;
  const frac = ((m[3] || '') + '00').slice(0, 2);
  return m[1] + m[2] + sep + frac;
}

/**
 * Vertrag Y: right-click on a bank account card of Entreprise (`data-ctx="acct"`): standard bank, „Auf Dokumenten“,
 * copy the IBAN, remove (archived when it has statements or payments, 6 s to undo). Writes need `f_acct` EDIT.
 */
export function accountProvider(getLogic: () => ComptaLogic | null): CtxProvider {
  return (ev) => {
    const el = ev.target.closest<HTMLElement>('[data-ctx="acct"]'), logic = getLogic();
    const id = el?.dataset.ctxId;
    if (!el || !id || !logic?.firmA) return null;
    const a = logic.firmA.acctList().find((x: { id: string }) => x.id === id) as { id: string; bank: string; bankName: string; iban: string; def: boolean; onDocs: boolean; draft: boolean } | undefined;
    if (!a) return null;
    const edit = !!logic.can?.('f_acct'), name = a.bank === 'OTHER' ? a.bankName || tr2('Bankkonto', 'Compte bancaire') : a.bank;
    const out: Array<CtxEntry | false> = [
      { header: name },
      !a.draft && { label: a.def ? tr2('Standardbank', 'Banque par défaut') : tr2('Als Standardbank festlegen', 'Définir comme banque par défaut'), icon: 'check', disabled: !edit || a.def, run: () => logic.firmA.makeDefaultAccount(a.id) },
      !a.draft && { label: a.onDocs ? tr2('Nicht mehr auf Dokumenten', 'Retirer des documents') : tr2('Auf Dokumenten zeigen', 'Afficher sur les documents'), icon: 'docs', disabled: !edit, run: () => logic.firmA.toggleOnDocuments(a.id) },
      !!a.iban && { label: tr2('IBAN kopieren', 'Copier l’IBAN'), icon: 'copy', hint: a.iban.replace(/\s/g, '').slice(-4), run: () => copyAndTell(a.iban.replace(/\s/g, ''), 'IBAN') },
      'sep',
      { label: a.draft ? tr2('Entwurf verwerfen', 'Supprimer le brouillon') : tr2('Konto entfernen …', 'Retirer le compte …'), icon: 'trash', danger: true, disabled: !edit, run: () => { logic.firmA.removeAccount(a.id); logic.setState?.((s: { fRev?: number }) => ({ fRev: (s.fRev || 0) + 1 })); } },
    ];
    return out;
  };
}

/**
 * D4: right-click on a month tile (`data-ctx="binder"`, compile patch) or on „Fiches de paie“ → „Étiquette de classeur …“:
 * the dialog for the binder spine labels of that month (factures reçues / émises) or of the year's pay slips and the
 * employee files. Offered when the person may read one of the kinds (d_in, d_out, d_pay, pers).
 */
export const binderProvider: CtxProvider = (ev) => {
  const el = ev.target.closest<HTMLElement>('[data-ctx="binder"]');
  const group = el?.dataset.ctxKind as BinderGroup | undefined;
  if (!el || (group !== 'inv' && group !== 'pay')) return null;
  const can = (k: string) => level(session.user, k as never) !== 'NONE';
  if (group === 'inv' ? !can('d_in') && !can('d_out') : !can('d_pay') && !can('pers')) return null;
  const year = el.dataset.ctxYear || String(new Date().getFullYear()), month = el.dataset.ctxMonth || undefined;
  const ym = month && /^\d{1,2}$/.test(month) ? `${year}-${month.padStart(2, '0')}` : null;
  return [
    { label: tr2('Ordneretikett …', 'Étiquette de classeur …'), icon: 'print', run: () => openBinder({ group, year, ...(month ? { month } : {}) }) },
    // ES correction: the month's paid incoming invoices – printed / filed, collective printing in binder order.
    group === 'inv' && !!ym && can('d_in') && { label: tr2('Druckübersicht …', 'Vue d’impression …'), icon: 'print', run: () => openPrintOverview(ym) },
    // WR: the month's recurring supplier invoices (expected, received, missing, hints).
    group === 'inv' && !!ym && can('d_in') && { label: tr2('Wiederkehrende Rechnungen …', 'Factures récurrentes …'), icon: 'reload', run: () => openRecurring(ym) },
  ];
};
